import { action, makeObservable, observable, computed } from "mobx";
import VaultStore from "./VaultStore";
import DocumentStore from "./documentStore";
import AgentStore from "./agentStore";

class KnowledgeStore {
    currentView = 'drives'
    selectedDrive = null
    selectedDriveTitle = 'Drive'
    searchQuery = ''
    vaultStore = null
    documentStore = null
    selectedDocumentsCount = 0
    selectedDrivesCount = 0
    selectedDrivesSet = new Set()  // ✅ PERFORMANCE: O(1) operations
    selectedDocumentsSet = new Set()  // ✅ PERFORMANCE: O(1) operations
    alert = {
        show: false,
        severity: 'info',
        description: '',
        autoClose: true,
        autoCloseDelay: 3000
    }
    isInitialized = false
    
    constructor(agentStore) {
        this.vaultStore = new VaultStore();
        this.documentStore = new DocumentStore();
        this.agentStore = agentStore;
        makeObservable(this, {
            currentView: observable,
            selectedDrive: observable,
            selectedDriveTitle: observable,
            searchQuery: observable,
            vaultStore: observable,
            documentStore: observable,
            selectedDocumentsCount: observable,
            selectedDrivesCount: observable,
            selectedDrivesSet: observable,
            selectedDocumentsSet: observable,
            alert: observable,
            isInitialized: observable,
            setCurrentView: action,
            setSelectedDrive: action,
            clearSelectedDrive: action,
            setSearchQuery: action,
            setSelectedDocumentsCount: action,
            setSelectedDrivesCount: action,
            toggleDriveSelection: action,
            toggleDocumentSelection: action,
            clearDocumentSelections: action,
            clearDriveSelections: action,
            resetDocumentList: action,
            fetchVaultList: action,
            fetchDocumentList: action,
            showAlert: action,
            hideAlert: action,
            // Computed properties
            selectedDrives: computed,
            selectedDocuments: computed,
        });
    }
    
    setCurrentView = (view) => {
        this.currentView = view
    }
    
    setSelectedDrive = (drive) => {
        this.selectedDrive = drive
        this.selectedDriveTitle = drive?.name || 'Drive'
        this.currentView = 'documents'
    }
    
    clearSelectedDrive = () => {
        this.selectedDrive = null
        this.selectedDriveTitle = 'Drive'
        this.currentView = 'drives'
    }
    
    setSearchQuery = (query) => {
        this.searchQuery = query
    }

    setSelectedDocumentsCount = (count) => {
        this.selectedDocumentsCount = count
    }

    setSelectedDrivesCount = (count) => {
        this.selectedDrivesCount = count
    }

    // Computed: Convert Set to Array for UI compatibility
    get selectedDrives() {
        return Array.from(this.selectedDrivesSet);
    }

    get selectedDocuments() {
        return Array.from(this.selectedDocumentsSet);
    }

    // ✅ PERFORMANCE: O(1) selection operations
    toggleDriveSelection = (vaultUuid) => {
        if (this.selectedDrivesSet.has(vaultUuid)) {  // O(1) lookup
            this.selectedDrivesSet.delete(vaultUuid);  // O(1) deletion
        } else {
            this.selectedDrivesSet.add(vaultUuid);     // O(1) addition
        }
        this.selectedDrivesCount = this.selectedDrivesSet.size;
    }

    toggleDocumentSelection = (documentUuid) => {
        if (this.selectedDocumentsSet.has(documentUuid)) {  // O(1) lookup
            this.selectedDocumentsSet.delete(documentUuid);  // O(1) deletion
        } else {
            this.selectedDocumentsSet.add(documentUuid);     // O(1) addition
        }
        this.selectedDocumentsCount = this.selectedDocumentsSet.size;
    }
    
    
    onGrantAccess = async ({ agentId, userId }) => {
        let hasErrors = false;
        let errorMessages = [];
        let successCount = 0;

        for (const drive of this.selectedDrives) {
            try {
                const body = {
                    agentUuid: agentId,
                    vaultUuid: drive,
                    tenant_id: "test123",
                    grantedBy: userId,
                    owner: "user123",
                    createdBy: "user123",
                    privilege: "reader",
                    description: "Agent has read access to vault documents",
                    expiresAt: "2024-12-31T23:59:59Z",
                    scope: "global",
                    category: "document_access"
                }
                const response = await this.agentStore.assignVaultAgent(body);
                if (response?.status === 200 || response?.status === 201) {
                    successCount++;
                }
            } catch (error) {
                hasErrors = true;
                const errorMessage = error.response?.data?.message || "Error assigning agent to drive";
                errorMessages.push(errorMessage);
            }
        }

        for (const document of this.selectedDocuments) {
            try {
                const body = {
                    document_uuid: document,
                    agent_uuid: agentId,
                    expires_at: "2025-07-10T10:30:00Z",
                    owner: "user123",
                    created_by: "user123",
                    description: "Assign agent to document",
                    granted_by: userId,
                    privilege: "reader"
                }
                const response = await this.agentStore.assignDocumentAgent(body);
                if (response?.status === 200 || response?.status === 201) {
                    successCount++;
                }
            } catch (error) {
                hasErrors = true;
                const errorMessage = error.response?.data?.message || "Error assigning agent to document";
                errorMessages.push(errorMessage);
            }
        }
        if (!hasErrors) {
            await this.agentStore.fetchDrives(agentId);
            await this.agentStore.fetchDocuments(agentId);
        }
        

        return {
            success: !hasErrors,
            totalProcessed: this.selectedDrives.length + this.selectedDocuments.length,
            successCount,
            errorMessages: errorMessages.length > 0 ? errorMessages : null
            
        };
    }
   

    clearDriveSelections = () => {
        this.selectedDrivesSet.clear();  // O(1) clear
        this.selectedDrivesCount = 0;
    }

    clearDocumentSelections = () => {
        this.selectedDocumentsSet.clear();  // O(1) clear
        this.selectedDocumentsCount = 0;
    }

    resetDocumentList = () => {
        this.documentStore.resetState();
    }


    setInitialized = (value) => {
        this.isInitialized = value
    }

    // Método para inicializar el store
    initializeStore = async () => {
        if (this.isInitialized) return;
        
        try {
            // Si ya hay datos en documentStore, marcar como inicializado
            if (this.documentStore.hasLoadedData()) {
                console.log('DocumentStore already has data, marking as initialized');
                this.setInitialized(true);
                return;
            }
            
            // Inicializar vaults si es necesario
            if (this.vaultStore.vaultList.data.length === 0) {
                await this.vaultStore.fetchVaultList({
                    tenantId: "1",
                    page_size: 100
                });
            }
            
            this.setInitialized(true);
        } catch (error) {
            console.warn('Error initializing knowledge store:', error);
            // Marcar como inicializado para evitar loops infinitos
            this.setInitialized(true);
        }
    }

    fetchVaultList = async (params) => {
        return await this.vaultStore.fetchVaultList(params);
    }

    fetchDocumentList = async (params) => {
        return await this.documentStore.fetchDocumentList(params);
    }

    get isDrivesView() {
        return this.currentView === 'drives'
    }
    
    get isDocumentsView() {
        return this.currentView === 'documents'
    }
    
    get canGoBack() {
        return this.currentView === 'documents' && this.selectedDrive !== null
    }

    showAlert = ({ severity, description, autoClose = true, autoCloseDelay = 3000 }) => {
        this.alert = {
            show: true,
            severity,
            description,
            autoClose,
            autoCloseDelay
        };

        if (autoClose) {
            setTimeout(() => {
                this.hideAlert();
            }, autoCloseDelay);
        }
    }

    hideAlert = () => {
        this.alert = {
            show: false,
            severity: 'info',
            description: '',
            autoClose: true,
            autoCloseDelay: 3000
        };
    }

    onRemoveAccess = async (agentId) => {
        let results = {
            success: 0,
            failed: 0,
            errorMessages: []
        };

        try {
           
            if (this.selectedDrives.length > 0) {
                const drivesResult = await this.agentStore.unassignAgentFromMultipleDrives(
                    this.selectedDrives,
                    agentId
                );
                results.success += drivesResult.success;
                results.failed += drivesResult.failed;
                if (drivesResult.errors?.length > 0) {
                    results.errorMessages.push(...drivesResult.errors.map(e => 
                        `Failed to remove access to drive ${e.uuid}: ${e.error?.message || 'Unknown error'}`
                    ));
                }
            }


            if (this.selectedDocuments.length > 0) {
                const documentsResult = await this.agentStore.unassignAgentFromMultipleDocuments(
                    this.selectedDocuments,
                    agentId
                );
                results.success += documentsResult.success;
                results.failed += documentsResult.failed;
                if (documentsResult.errors?.length > 0) {
                    results.errorMessages.push(...documentsResult.errors.map(e => 
                        `Failed to remove access to document ${e.uuid}: ${e.error?.message || 'Unknown error'}`
                    ));
                }
            }

        
            this.clearDriveSelections();
            this.clearDocumentSelections();

            if (results.failed > 0) {
                this.showAlert({
                    severity: 'error',
                    description: `Failed to remove access to ${results.failed} items. ${results.success} items were processed successfully.`
                });
            } else {
                this.showAlert({
                    severity: 'success',
                    description: `Successfully removed access to ${results.success} items.`
                });
            }

            return results;
        } catch (error) {
            console.error('Error in onRemoveAccess:', error);
            this.showAlert({
                severity: 'error',
                description: 'An error occurred while removing access.'
            });
            throw error;
        }
    }
}

export default KnowledgeStore